import { useEffect, useState } from 'react';

import { useSplitAndHeap } from '@/hooks/useSplitAndHeap';

import { HeadingText } from '@/components/atoms/HeadingText';
import { WizardBox } from '@/components/WizardBox/WizardBox';
import {
	IconImageFile,
	IconHVACUnit,
	IconInfoBubble,
	IconRuler,
} from '@/components/icons';
import { TextAlert } from '@/components/calculators/TextAlert';

import { SubStepOpts } from '../../PictureStep';
import { AvailableColors } from '@/components/Badge';
interface DefaultProps {
	handleClick: (show: SubStepOpts) => void;
}

type BadgeProps = {
	label: string;
	style: AvailableColors | undefined;
};

type Badge = {
	show: boolean;
	photo: BadgeProps | null;
	model: BadgeProps | null;
	sizeType: BadgeProps | null;
};

const Default = (props: DefaultProps) => {
	const { handleClick } = props;

	const [badges, setBadges] = useState<Badge>({
		show: false,
		photo: null,
		model: null,
		sizeType: null,
	});

	const { SystemDetailsBadges_InPersonReplace } = useSplitAndHeap(
		'SystemDetailsBadges_InPersonReplace',
	);
	const { treatment, config } = SystemDetailsBadges_InPersonReplace;

	useEffect(() => {
		if (config && treatment === 'on') {
			// It looks like they might add color names in the JSON object that are different from what we need for our color palette.
			const colorMatch: { [key: string]: string } = {
				gradient: 'hvac',
				green: 'lime',
				blue: 'sky',
				yellow: 'amber',
				gray: 'slate',
			};

			// This will ensure the colors will match to what we need.
			const changeColorNames = config
				.replace(
					/\b(green|blue|yellow|gradient|gray)\b/gi,
					(match) => colorMatch[match],
				)
				.replace('sizetype', 'sizeType'); // In canse they forget make it cammel case

			const badgesObject = JSON.parse(changeColorNames);

			if (badgesObject.photo && badgesObject.model && badgesObject.sizeType) {
				// All poptions have to exist for the badges
				setBadges((prev) => ({ ...prev, ...badgesObject }));

				// Even if the key exists, if its label is empty, we still won't show the badge
				// I will defatul the style to slate in the JSX
				if (
					badgesObject.photo.label !== '' &&
					badgesObject.model !== '' &&
					badgesObject.sizeType !== ''
				) {
					setBadges((prev) => ({ ...prev, show: true }));
				}
			}
		}
	}, [config, treatment]);

	return (
		<>
			<HeadingText
				tag="h1"
				size="h2"
				className="mb-10 mt-10 lg:mb-14 lg:mt-20"
				balanced
			>
				We need a few details about your HVAC system
			</HeadingText>

			<div className="m-auto mb-8 flex flex-col justify-between gap-4 md:flex-row lg:mb-10 lg:w-4/5 lg:gap-6">
				<WizardBox
					onClick={() => handleClick('upload')}
					label="Upload label photo"
					description="of my outside HVAC label"
					icon={<IconImageFile className="h-8 w-8 lg:h-10 lg:w-10" />}
					badgeText={badges.show ? badges.photo?.label : undefined}
					badgeColor={badges.show ? badges.photo?.style || 'slate' : undefined}
				/>

				<WizardBox
					onClick={() => handleClick('model-number')}
					label="Enter model number"
					description="of my outside HVAC unit"
					icon={<IconHVACUnit className="h-8 w-8 lg:h-10 lg:w-10" />}
					badgeText={badges.show ? badges.model?.label : undefined}
					badgeColor={badges.show ? badges.model?.style || 'slate' : undefined}
				/>
				<WizardBox
					onClick={() => handleClick('type-size')}
					label="Select type and size"
					description="of my outside HVAC unit"
					icon={<IconRuler className="h-8 w-8 fill-black lg:h-10 lg:w-10" />}
					badgeText={badges.show ? badges.sizeType?.label : undefined}
					badgeColor={
						badges.show ? badges.sizeType?.style || 'slate' : undefined
					}
				/>
			</div>
			<div className="mb-8 lg:mb-10">
				<TextAlert
					renderIcon={IconInfoBubble}
					title="Why are we asking?"
					text="We use system size and efficiency details to send the right local expert to your home."
				/>
			</div>
		</>
	);
};

export default Default;
